import { useEffect, useState } from 'react';
import { Trophy, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { getToken } from '../../lib/auth';
import { Link } from 'react-router-dom';

interface LoyaltyScoreWidgetProps {
  title?: string;
  /** 'monthly' | 'weekly' — defaults to 'monthly' */
  periodType?: string;
  programId?: string;
}

interface LeaderboardRank {
  rank: number | null;
  score: number;
  totalParticipants: number;
}

/**
 * CMS block: loyalty_score_widget
 * Shows the authenticated customer's current leaderboard score and rank
 * for the configured period (monthly or weekly).
 */
export function LoyaltyScoreWidget({ props }: { props: LoyaltyScoreWidgetProps }) {
  const [data, setData] = useState<LeaderboardRank | null>(null);
  const [loading, setLoading] = useState(true);
  const authed = !!getToken();

  const periodType = props.periodType ?? 'monthly';

  useEffect(() => {
    if (!authed || !props.programId) {
      setLoading(false);
      return;
    }

    api
      .get<{ periodType: string; periodKey: string; myRank: LeaderboardRank }>(
        `/customer/loyalty/leaderboard?programId=${props.programId}&periodType=${periodType}&limit=1`,
      )
      .then((res) => {
        setData(res.data.myRank);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authed, props.programId, periodType]);

  if (!authed) {
    return (
      <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-5 py-6 text-center">
        <Trophy size={24} className="mx-auto mb-2 text-zinc-300" />
        <p className="text-sm text-zinc-500">
          <Link to="/login" className="underline hover:no-underline text-zinc-700 font-medium">
            Log in
          </Link>{' '}
          to see your ranking
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 size={18} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!data || data.score === 0) {
    return (
      <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-5 py-6 text-center">
        <Trophy size={24} className="mx-auto mb-2 text-zinc-300" />
        <p className="text-sm text-zinc-500">No leaderboard score yet. Start earning points!</p>
      </div>
    );
  }

  const periodLabel = periodType === 'weekly' ? 'this week' : 'this month';

  return (
    <div className="bg-white border border-zinc-100 rounded-2xl p-5 shadow-sm">
      {props.title && <h3 className="text-sm font-semibold text-zinc-700 mb-3">{props.title}</h3>}

      <div className="flex items-center gap-4">
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
          <Trophy size={20} className="text-amber-500" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-2xl font-extrabold text-zinc-900 leading-none">
            {data.score.toLocaleString()}
          </p>
          <p className="text-xs text-zinc-500 mt-0.5">score {periodLabel}</p>
        </div>

        {data.rank !== null && (
          <div className="text-right flex-shrink-0">
            <p className="text-lg font-bold text-zinc-700">#{data.rank}</p>
            <p className="text-xs text-zinc-400">of {data.totalParticipants.toLocaleString()}</p>
          </div>
        )}
      </div>
    </div>
  );
}
